import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Link as ChakraLink,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";
import { FiEdit2, FiGlobe, FiLock, FiPlus, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import { trackEvent } from "../../../shared/analytics";
import SectionTitle from "../../../shared/components/SectionTitle";
import {
  createComicNote,
  deleteComicNote,
  updateComicNote,
} from "../api/comicNotesApi";

const NOTE_INPUT_MIN_HEIGHT = 112;
const NOTE_INPUT_MAX_HEIGHT = 180;

const resizeTextArea = (input) => {
  if (!input) {
    return;
  }

  input.style.height = "auto";
  const height = Math.min(
    NOTE_INPUT_MAX_HEIGHT,
    Math.max(NOTE_INPUT_MIN_HEIGHT, input.scrollHeight)
  );
  input.style.height = `${height}px`;
};

const formatRelativeTime = (value) => {
  if (!value) {
    return "just now";
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return "just now";
  }

  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - timestamp) / 1000)
  );
  const units = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];

  const unit = units.find(([, seconds]) => elapsedSeconds >= seconds);
  if (!unit) {
    return "just now";
  }

  const [name, seconds] = unit;
  const count = Math.floor(elapsedSeconds / seconds);
  return `${count} ${name}${count === 1 ? "" : "s"} ago`;
};

const NotesHeading = ({ title, count }) => (
  <Flex align="center" justify="space-between">
    <SectionTitle my="0">{title}</SectionTitle>
    {count !== undefined && (
      <Text color="#8F9BB3" fontSize="14px" fontWeight="600">
        {count}
      </Text>
    )}
  </Flex>
);

const PrivacyOption = ({ icon, label, description, selected, onClick }) => (
  <Flex
    as="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    flex="1"
    minH="58px"
    px={{ base: "10px", md: "14px" }}
    align="center"
    textAlign="left"
    borderWidth="1px"
    borderColor={selected ? "#ED1D24" : "#30384E"}
    borderRadius="10px"
    bg={selected ? "#241A27" : "transparent"}
    _hover={{ bg: selected ? "#241A27" : "#1A2138" }}
    _focusVisible={{ outline: "2px solid #ED1D24", outlineOffset: "2px" }}
  >
    <Icon
      as={icon}
      flexShrink={0}
      color={selected ? "#ED1D24" : "#A8B2C8"}
      boxSize="20px"
    />
    <Box flex="1" minW="0" ml="8px">
      <Text color="#F7F9FC" fontSize="14px" lineHeight="20px">
        {label}
      </Text>
      <Text color="#8F9BB3" fontSize="12px" lineHeight="16px">
        {description}
      </Text>
    </Box>
    <Box
      flexShrink={0}
      w="18px"
      h="18px"
      borderWidth="2px"
      borderColor={selected ? "#ED1D24" : "#58627A"}
      borderRadius="full"
      position="relative"
    >
      {selected && (
        <Box
          position="absolute"
          top="3px"
          left="3px"
          w="8px"
          h="8px"
          borderRadius="full"
          bg="#ED1D24"
        />
      )}
    </Box>
  </Flex>
);

const NoteAction = ({ icon, label, onClick, danger = false, isDisabled }) => (
  <Button
    aria-label={label}
    isDisabled={isDisabled}
    leftIcon={<Icon as={icon} boxSize="16px" />}
    onClick={onClick}
    size="sm"
    variant="ghost"
    color={danger ? "#ED1D24" : "#A8B2C8"}
    px="2px"
    minH="40px"
    _hover={{ bg: "#222B45" }}
  >
    {label}
  </Button>
);

const NoteAuthor = ({ author, timestamp, children }) => {
  const authorName = author?.userHandle || "Redwing reader";

  return (
    <Flex align="center" gap={{ base: "12px", md: "14px" }}>
      <Avatar
        flexShrink={0}
        size="sm"
        name={authorName}
        src={author?.profilePicture}
      />
      <Flex flex="1" minW="0" align="center" gap="8px" flexWrap="wrap">
        {author?.userHandle ? (
          <ChakraLink
            as={Link}
            to={`/profile/${author.userHandle}`}
            color="#F7F9FC"
            fontSize="14px"
            fontWeight="700"
            _hover={{ textDecoration: "underline" }}
          >
            {author.userHandle}
          </ChakraLink>
        ) : (
          <Text color="#F7F9FC" fontSize="14px" fontWeight="700">
            {authorName}
          </Text>
        )}
        <Text color="#8F9BB3" fontSize="12px">
          · {formatRelativeTime(timestamp)}
        </Text>
        {children}
      </Flex>
    </Flex>
  );
};

const OwnNote = ({ note, author, onEdit, onDelete, deleting }) => {
  const isPublic = note.privacy === "public";

  return (
    <Box
      as="article"
      mt="14px"
      p="16px"
      borderWidth="1px"
      borderColor="#30384E"
      borderRadius="14px"
      bg="#1A2138"
    >
      <NoteAuthor
        author={note.author || author}
        timestamp={note.updatedAt || note.createdAt}
      >
        <Flex align="center" gap="5px">
          <Icon
            as={isPublic ? FiGlobe : FiLock}
            color={isPublic ? "#27AE60" : "#A8B2C8"}
            boxSize="15px"
          />
          <Text color="#A8B2C8" fontSize="12px" fontWeight="700">
            {isPublic ? "Public" : "Private"}
          </Text>
        </Flex>
      </NoteAuthor>
      <Text
        mt="10px"
        color="#F7F9FC"
        fontSize="15px"
        lineHeight="23px"
        whiteSpace="pre-wrap"
      >
        {note.text}
      </Text>
      <Flex mt="10px" gap="12px">
        <NoteAction icon={FiEdit2} label="Edit" onClick={onEdit} />
        <NoteAction
          danger
          icon={FiTrash2}
          label="Delete"
          onClick={() => onDelete(note)}
          isDisabled={deleting}
        />
      </Flex>
    </Box>
  );
};

const ReaderNote = ({ note }) => (
  <Box as="article" py="16px" borderBottomWidth="1px" borderColor="#252E49">
    <NoteAuthor author={note.author} timestamp={note.createdAt} />
    <Text
      mt="8px"
      color="#F7F9FC"
      fontSize="15px"
      lineHeight="23px"
      whiteSpace="pre-wrap"
    >
      {note.text}
    </Text>
  </Box>
);

const NoteComposer = ({
  editing,
  draft,
  privacy,
  inputRef,
  saving,
  onChange,
  onPrivacyChange,
  onCancel,
  onSave,
}) => {
  const resizeInput = () => resizeTextArea(inputRef.current);

  useEffect(() => {
    resizeTextArea(inputRef.current);
    inputRef.current?.focus();
  }, [editing, inputRef]);

  return (
    <Box mt="14px">
      <Textarea
        ref={inputRef}
        aria-label="Note text"
        autoFocus
        maxLength={10000}
        minH={`${NOTE_INPUT_MIN_HEIGHT}px`}
        onChange={(event) => {
          onChange(event.target.value);
          resizeInput();
        }}
        onInput={resizeInput}
        overflowY="auto"
        placeholder="Write a note, theory, or anything you want to remember"
        resize="none"
        value={draft}
      />
      <Text
        mt="16px"
        mb="8px"
        color="#8F9BB3"
        fontSize="11px"
        fontWeight="700"
        letterSpacing="0.8px"
      >
        WHO CAN SEE THIS?
      </Text>
      <Flex gap="8px">
        <PrivacyOption
          description="Only you"
          icon={FiLock}
          label="Private"
          onClick={() => onPrivacyChange("private")}
          selected={privacy === "private"}
        />
        <PrivacyOption
          description="All readers"
          icon={FiGlobe}
          label="Public"
          onClick={() => onPrivacyChange("public")}
          selected={privacy === "public"}
        />
      </Flex>
      <Flex mt="14px" justify="flex-end" gap="8px">
        <Button
          isDisabled={saving}
          minH="44px"
          onClick={onCancel}
          variant="ghost"
          color="#A8B2C8"
          _hover={{ bg: "#222B45" }}
        >
          Cancel
        </Button>
        <Button
          colorScheme="red"
          color="white"
          isDisabled={saving || !draft.trim()}
          isLoading={saving}
          minH="44px"
          onClick={onSave}
        >
          {editing ? "Save changes" : "Save note"}
        </Button>
      </Flex>
    </Box>
  );
};

const ComicNotes = forwardRef(
  (
    {
      comicId,
      notes = [],
      authData,
      onRequireAuth,
      showOwnNoteSection = false,
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const leastDestructiveRef = useRef(null);
    const queryClient = useQueryClient();
    const toast = useToast({ duration: 5000, isClosable: true });
    const [composerOpen, setComposerOpen] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [draft, setDraft] = useState("");
    const [privacy, setPrivacy] = useState("private");
    const [noteToDelete, setNoteToDelete] = useState(null);

    const invalidateComic = () => {
      queryClient.invalidateQueries(["comic", comicId]);
    };

    const closeComposer = () => {
      setComposerOpen(false);
      setEditingNote(null);
      setDraft("");
      setPrivacy("private");
    };

    const showMutationError = (title, error) => {
      toast({
        title,
        description:
          error?.response?.data?.errors?.text?.[0] ||
          error?.response?.data?.message ||
          "Please try again.",
        status: "error",
      });
    };

    const createMutation = useMutation(
      ({ text, notePrivacy }) => createComicNote(comicId, text, notePrivacy),
      {
        onSuccess: (_, variables) => {
          trackEvent("comic_note_created", {
            comic_id: comicId,
            privacy: variables?.notePrivacy,
          });
          closeComposer();
          invalidateComic();
          toast({
            title: "Note saved",
            description: "Your note has been added to this comic.",
            status: "success",
          });
        },
        onError: (error) => showMutationError("Could not save note", error),
      }
    );
    const updateMutation = useMutation(
      ({ noteId, text, notePrivacy }) =>
        updateComicNote(comicId, noteId, text, notePrivacy),
      {
        onSuccess: (_, variables) => {
          trackEvent("comic_note_updated", {
            comic_id: comicId,
            privacy: variables?.notePrivacy,
          });
          closeComposer();
          invalidateComic();
          toast({
            title: "Note updated",
            description: "Your changes have been saved.",
            status: "success",
          });
        },
        onError: (error) => showMutationError("Could not update note", error),
      }
    );
    const deleteMutation = useMutation(
      ({ noteId }) => deleteComicNote(comicId, noteId),
      {
        onSuccess: () => {
          trackEvent("comic_note_deleted", { comic_id: comicId });
          setNoteToDelete(null);
          invalidateComic();
          toast({
            title: "Note deleted",
            description: "Your note has been removed from this comic.",
            status: "success",
          });
        },
        onError: (error) => showMutationError("Could not delete note", error),
      }
    );

    const openComposer = (note) => {
      if (!authData) {
        onRequireAuth?.();
        return;
      }

      setEditingNote(note || null);
      setDraft(note?.text || "");
      setPrivacy(note?.privacy === "public" ? "public" : "private");
      setComposerOpen(true);
    };

    useImperativeHandle(ref, () => ({
      openComposer: () => openComposer(),
    }));

    const saveNote = () => {
      const text = draft.trim();
      if (!text || createMutation.isLoading || updateMutation.isLoading) {
        return;
      }

      if (editingNote) {
        updateMutation.mutate({
          noteId: editingNote.id,
          notePrivacy: privacy,
          text,
        });
        return;
      }

      createMutation.mutate({ notePrivacy: privacy, text });
    };

    const safeNotes = Array.isArray(notes) ? notes : [];
    const ownNotes = safeNotes.filter((note) => note.isMine || note.is_mine);
    const readerNotes = safeNotes.filter(
      (note) => !note.isMine && !note.is_mine && note.privacy === "public"
    );

    return (
      <Box px="24px" pt="28px" pb="32px">
        {showOwnNoteSection && (
          <Box>
            <NotesHeading title="YOUR NOTE" />
            {ownNotes.map((note) => (
              <OwnNote
                key={note.id}
                author={authData}
                deleting={deleteMutation.isLoading}
                note={note}
                onDelete={setNoteToDelete}
                onEdit={() => openComposer(note)}
              />
            ))}
            {composerOpen ? (
              <NoteComposer
                draft={draft}
                editing={!!editingNote}
                inputRef={inputRef}
                onCancel={closeComposer}
                onChange={setDraft}
                onPrivacyChange={setPrivacy}
                onSave={saveNote}
                privacy={privacy}
                saving={createMutation.isLoading || updateMutation.isLoading}
              />
            ) : !ownNotes.length ? (
              <Flex
                as="button"
                type="button"
                align="center"
                w="100%"
                minH="84px"
                mt="14px"
                px="20px"
                textAlign="left"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="#3C3C3C"
                borderRadius="14px"
                bg="transparent"
                onClick={() => openComposer()}
                _hover={{ bg: "#1A2138", borderColor: "#ED1D24" }}
                _focusVisible={{
                  outline: "2px solid #ED1D24",
                  outlineOffset: "2px",
                }}
              >
                <Icon as={FiPlus} color="#A8B2C8" boxSize="22px" />
                <Text ml="14px" color="#A8B2C8" fontSize="15px">
                  {authData ? "Add a note or theory" : "Sign in to add a note"}
                </Text>
              </Flex>
            ) : null}
          </Box>
        )}

        <Box mt={showOwnNoteSection ? "32px" : "0"}>
          <NotesHeading count={readerNotes.length} title="NOTES FROM READERS" />
          {readerNotes.length ? (
            <Box>
              {readerNotes.map((note) => (
                <ReaderNote key={note.id} note={note} />
              ))}
            </Box>
          ) : (
            <Text mt="14px" color="#8F9BB3" fontSize="15px" lineHeight="23px">
              No notes from readers yet.
            </Text>
          )}
        </Box>

        <AlertDialog
          isOpen={!!noteToDelete}
          leastDestructiveRef={leastDestructiveRef}
          onClose={() => {
            if (!deleteMutation.isLoading) {
              setNoteToDelete(null);
            }
          }}
          isCentered
        >
          <AlertDialogOverlay bg="rgba(0, 0, 0, 0.75)" />
          <AlertDialogContent
            bg="#171C2D"
            borderWidth="1px"
            borderColor="#30384E"
            borderRadius="14px"
            mx="4"
          >
            <AlertDialogHeader color="#F7F9FC">
              Delete your note?
            </AlertDialogHeader>
            <AlertDialogBody color="#A8B2C8">
              This note will be permanently deleted.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={leastDestructiveRef}
                isDisabled={deleteMutation.isLoading}
                onClick={() => setNoteToDelete(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={deleteMutation.isLoading}
                ml="3"
                onClick={() =>
                  deleteMutation.mutate({ noteId: noteToDelete.id })
                }
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Box>
    );
  }
);

export default ComicNotes;
